import { useCallback, useEffect } from 'react';
import {
  Link as RouterLink,
  Navigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { providersService } from '../../shared/services/providers.service';
import { imageSrcSet, imageLargeSrc } from '../../shared/utils/images';

function formatPrice(cents?: number | null, currency?: string | null) {
  if (cents == null || !currency) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function GalleryPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useQuery({
    queryKey: ['provider', id],
    queryFn: () => providersService.get(id),
    enabled: Boolean(id),
  });
  const p = query.data;

  // Lightbox state lives in the URL so refresh / share / back-button all work.
  const photoParam = searchParams.get('photo');
  const parsedIndex = photoParam ? parseInt(photoParam, 10) : NaN;
  const portfolioLength = p?.portfolio?.length ?? 0;
  const lightboxIndex =
    Number.isFinite(parsedIndex) && parsedIndex >= 0 && parsedIndex < portfolioLength
      ? parsedIndex
      : null;
  const lightboxImage = lightboxIndex != null ? p?.portfolio[lightboxIndex] : null;

  const openLightbox = useCallback(
    (idx: number) => {
      const next = new URLSearchParams(searchParams);
      next.set('photo', String(idx));
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  const closeLightbox = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('photo');
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  const stepLightbox = useCallback(
    (delta: number) => {
      if (lightboxIndex == null || portfolioLength === 0) return;
      const nextIndex = (lightboxIndex + delta + portfolioLength) % portfolioLength;
      const next = new URLSearchParams(searchParams);
      next.set('photo', String(nextIndex));
      setSearchParams(next, { replace: true });
    },
    [lightboxIndex, portfolioLength, searchParams, setSearchParams],
  );

  // Keyboard nav for the lightbox.
  useEffect(() => {
    if (lightboxIndex == null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') stepLightbox(-1);
      else if (e.key === 'ArrowRight') stepLightbox(1);
      else if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIndex, stepLightbox, closeLightbox]);

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!p) return <Navigate to="/" replace />;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      {/* HEADER */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ mb: { xs: 3, md: 4 } }}
      >
        <IconButton
          component={RouterLink}
          to={`/providers/${p.id}`}
          aria-label={t('common.back')}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <ArrowBackRoundedIcon />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: '0.12em', fontWeight: 700, display: 'block', lineHeight: 1.2 }}
          >
            {t('provider.galleryEyebrow', { count: p.portfolio.length })}
          </Typography>
          <Typography variant="h4" noWrap sx={{ mt: 0.25 }}>
            {p.businessName}
          </Typography>
        </Box>
      </Stack>

      {/* GRID */}
      {p.portfolio.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: { xs: 6, md: 10 },
            px: 2,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 4,
          }}
        >
          <Typography color="text.secondary">{t('provider.noPortfolio')}</Typography>
        </Box>
      ) : (
        <Grid container spacing={{ xs: 1.5, sm: 2.5 }}>
          {p.portfolio.map((img, i) => {
            // Layout pattern: every 3rd image is full-width; the others are
            // paired side-by-side. Yields an alternating full / pair / full
            // rhythm reminiscent of editorial portfolio layouts.
            const isFull = i % 3 === 0;
            const priceLabel = formatPrice(img.priceCents, img.currency);
            return (
              <Grid
                item
                xs={12}
                sm={isFull ? 12 : 6}
                key={img.id}
              >
                <Box
                  onClick={() => openLightbox(i)}
                  sx={{
                    position: 'relative',
                    aspectRatio: isFull ? '4 / 3' : '1 / 1',
                    borderRadius: 2,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    boxShadow: (theme) =>
                      theme.palette.mode === 'dark'
                        ? '0 6px 18px rgba(0,0,0,0.45)'
                        : '0 6px 18px rgba(26,24,22,0.08)',
                    transition: 'transform 250ms ease, box-shadow 250ms ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: (theme) =>
                        theme.palette.mode === 'dark'
                          ? '0 14px 32px rgba(0,0,0,0.55)'
                          : '0 14px 32px rgba(26,24,22,0.14)',
                    },
                    '&:hover img': { transform: 'scale(1.04)' },
                    '&:hover .gallery-overlay': { opacity: 1 },
                  }}
                >
                  <Box
                    component="img"
                    src={imageSrcSet(img.imageUrl).src}
                    srcSet={imageSrcSet(img.imageUrl).srcSet}
                    sizes="(max-width:600px) 100vw, 50vw"
                    alt={img.caption ?? p.businessName}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      transition: 'transform 600ms ease',
                    }}
                  />

                  {priceLabel && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 14,
                        right: 14,
                        px: 1.5,
                        py: 0.6,
                        borderRadius: 1.75,
                        bgcolor: 'rgba(0,0,0,0.65)',
                        backdropFilter: 'blur(6px)',
                        color: 'common.white',
                        fontWeight: 700,
                        fontSize: isFull ? '1rem' : '0.875rem',
                      }}
                    >
                      {priceLabel}
                    </Box>
                  )}

                  {img.caption && (
                    <Box
                      className="gallery-overlay"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'flex-end',
                        p: { xs: 2, sm: 2.5 },
                        background:
                          'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.78) 100%)',
                        opacity: { xs: 1, md: 0 },
                        transition: 'opacity 250ms ease',
                      }}
                    >
                      <Typography
                        variant={isFull ? 'subtitle1' : 'body2'}
                        sx={{
                          color: 'common.white',
                          fontWeight: isFull ? 700 : 500,
                          lineHeight: 1.4,
                        }}
                      >
                        {img.caption}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* LIGHTBOX */}
      <Dialog
        open={Boolean(lightboxImage)}
        onClose={closeLightbox}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            m: { xs: 1, md: 3 },
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {/* Close */}
          <IconButton
            onClick={closeLightbox}
            aria-label={t('common.cancel')}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.55)',
              color: 'common.white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
              zIndex: 2,
            }}
          >
            <CloseRoundedIcon />
          </IconButton>

          {/* Prev / Next */}
          {portfolioLength > 1 && (
            <>
              <IconButton
                onClick={() => stepLightbox(-1)}
                aria-label={t('booking.previousWeek')}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 8,
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: 'common.white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                  zIndex: 2,
                }}
              >
                <ChevronLeftRoundedIcon />
              </IconButton>
              <IconButton
                onClick={() => stepLightbox(1)}
                aria-label={t('booking.nextWeek')}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: 8,
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: 'common.white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                  zIndex: 2,
                }}
              >
                <ChevronRightRoundedIcon />
              </IconButton>
            </>
          )}

          {lightboxImage && (
            <Box sx={{ position: 'relative' }}>
              <Box
                component="img"
                src={imageLargeSrc(lightboxImage.imageUrl)}
                alt={lightboxImage.caption ?? p.businessName}
                sx={{
                  width: '100%',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                  display: 'block',
                  borderRadius: 2,
                  bgcolor: 'rgba(0,0,0,0.4)',
                }}
              />

              {/* Caption + price + counter */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  p: { xs: 2, sm: 3 },
                  background:
                    'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 100%)',
                  color: 'common.white',
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'rgba(255,255,255,0.75)', display: 'block' }}
                  >
                    {(lightboxIndex ?? 0) + 1} / {portfolioLength}
                  </Typography>
                  {lightboxImage.caption && (
                    <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.25 }}>
                      {lightboxImage.caption}
                    </Typography>
                  )}
                </Box>
                {formatPrice(lightboxImage.priceCents, lightboxImage.currency) && (
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'common.white' }}>
                    {formatPrice(lightboxImage.priceCents, lightboxImage.currency)}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
